import React, { createContext, useContext, useCallback } from "react";
import { CardScore } from "../types/newtypes";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store/store";
import {
  incrementMistakes as incrementMistakesAction,
  finalizeCard as finalizeCardAction,
  resetAll as resetAllAction,
} from "../store/scoreTrackingSlice";

interface CardScoreData {
  card_id: string;
  mistakes: number;
  max_score: number;
  is_completed: boolean;
}

interface ScoreTrackingContextType {
  // Card score tracking
  cardScores: Map<string, CardScoreData>;

  // Core functions
  updateCardScore: (cardId: string, mistakes: number, maxScore: number) => void;
  getCardScore: (cardId: string) => CardScoreData | undefined;
  isCardCompleted: (cardId: string) => boolean;

  // Mistake tracking
  incrementMistakes: (cardId: string, maxScore: number) => void;
  getMistakes: (cardId: string) => number;

  // Lesson completion
  getFinalScores: () => { card_scores: CardScore[] };
  resetLessonScores: () => void;
}

const ScoreTrackingContext = createContext<
  ScoreTrackingContextType | undefined
>(undefined);

export const ScoreTrackingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();
  const byId = useSelector((state: RootState) => state.scoreTracking.byId);

  const cardScores = new Map<string, CardScoreData>(
    Object.entries(byId).map(([id, data]) => [id, data as CardScoreData])
  );

  const isCardCompleted = useCallback(
    (cardId: string) => Boolean(byId[cardId]?.is_completed),
    [byId]
  );

  const incrementMistakes = useCallback(
    (cardId: string, maxScore: number) => {
      if (byId[cardId]?.is_completed) return;
      dispatch(incrementMistakesAction({ cardId, maxScore }));
    },
    [dispatch, byId]
  );

  const getMistakes = useCallback(
    (cardId: string) => byId[cardId]?.mistakes || 0,
    [byId]
  );

  const updateCardScore = useCallback(
    (cardId: string, mistakes: number, maxScore: number) => {
      if (byId[cardId]?.is_completed) return;
      dispatch(finalizeCardAction({ cardId, mistakes, maxScore }));
    },
    [dispatch, byId]
  );

  const getCardScore = useCallback(
    (cardId: string) => (byId[cardId] as CardScoreData) || undefined,
    [byId]
  );

  const getFinalScores = useCallback(() => {
    const scores: CardScore[] = Object.values(byId)
      .filter((c) => c.is_completed)
      .map((c) => ({
        card_id: c.card_id,
        score: c.max_score,
        mistakes: c.mistakes,
      }));
    return { card_scores: scores };
  }, [byId]);

  const resetLessonScores = useCallback(() => {
    dispatch(resetAllAction());
  }, [dispatch]);

  const contextValue: ScoreTrackingContextType = {
    cardScores,
    updateCardScore,
    getCardScore,
    isCardCompleted,
    incrementMistakes,
    getMistakes,
    getFinalScores,
    resetLessonScores,
  };

  return (
    <ScoreTrackingContext.Provider value={contextValue}>
      {children}
    </ScoreTrackingContext.Provider>
  );
};

export const useScoreTracking = () => {
  const context = useContext(ScoreTrackingContext);
  if (!context) {
    throw new Error(
      "useScoreTracking must be used within a ScoreTrackingProvider"
    );
  }
  return context;
};
